import clsx from 'clsx'
import { Link } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, Pressable, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card, CardBody } from '@/src/elements/card'
import { Chip } from '@/src/elements/chip'
import { Image } from '@/src/elements/image'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useTranslate } from '@/src/lang/useTranslate'

import { CommunityMembersChip } from '../CommunityMembersChip'
import { CommunityNbDaysChip } from '../CommunityNbDaysChip'
import { CommunityActivityChip } from '../CommunityStatusSection/CommunityActivityChip'
import { CommunityUserChip } from '../CommunityUserChip'
import type { TCommunityCardBaseProps } from './CommunityCard.types'

export const CommunityCardBase = ({
  className,
  community,
  onCardPress,
  onSharePress,
  onSubscribePress,
  isLoading,
  fullWidth,
  showSubscribeButton = true,
  testId,
  href,
  subscription,
}: TCommunityCardBaseProps): ReactNode => {
  const displayShareButton = community.hasJoined && onSharePress
  const { t } = useTranslate()

  return (
    <Card
      testID={testId}
      additionalProps={{ role: 'link' }}
      onPress={onCardPress}
      isPressable={!!onCardPress}
      shadow="md"
      className={clsx(
        'native:w-[220px] native:h-[335px] native:max-h-[335px] native:max-w-[220px] min-w-[200px] max-w-[240px] flex-1 bg-white',
        fullWidth && 'native:min-w-full max-w-full',
        className,
      )}
    >
      <View className={clsx('relative h-[110px] max-w-[240px]', { 'native:min-w-full max-w-full': fullWidth })}>
        <Link href={href} asChild onPress={onCardPress}>
          <Pressable className="h-full w-full">
            <Image contentFit="cover" className="flex rounded-b-none" source={getImageUrl(community.picture)} />
          </Pressable>
        </Link>
        <View className="flex max-w-full flex-row items-start gap-2 absolute bottom-0 translate-y-1/2 px-2">
          <CommunityActivityChip community={community} subscription={subscription} colors="default" />
          {community.isPrivate && (
            <Chip text={t('community.private.private')} iconName={ICON.INCOGNITO} colors="default" variant="flat" />
          )}
        </View>
      </View>
      <CardBody className="items-left flex flex-col gap-3 p-4 pt-6">
        <Link onPress={onCardPress} href={href} asChild>
          <Pressable className="flex-1 flex-col gap-3">
            <Typography
              bold
              colors="primary"
              size="lg"
              className="native:min-h-[56px]"
              numberOfLines={Platform.OS === 'web' ? 3 : 2}
            >
              {community.name}
            </Typography>
            <CommunityUserChip community={community} className="native:max-w-full cursor-pointer border-none px-0" />
            <View className="native:flex-row native:flex-nowrap native:items-center native:justify-center flex w-full gap-1 px-2 pb-1 web:flex-row web:items-center web:justify-between">
              <CommunityMembersChip
                nbMembers={community.nbMembers}
                className="native:max-w-full cursor-pointer border-none px-0 bg-transparent"
              />
              <CommunityNbDaysChip
                community={community}
                className="native:max-w-full cursor-pointer border-none bg-transparent"
              />
            </View>
          </Pressable>
        </Link>
        {showSubscribeButton && (
          <Button
            variant="solid"
            colors={displayShareButton ? 'secondary' : 'primary'}
            fullWidth
            onPress={displayShareButton ? onSharePress : onSubscribePress}
            className="relative mt-auto flex flex-row items-center !justify-center"
            isLoading={isLoading}
          >
            {displayShareButton ? <FormattedMessage id="share.share" /> : <FormattedMessage id="words.i-subscribe" />}
          </Button>
        )}
      </CardBody>
    </Card>
  )
}
